/* eslint-disable @typescript-eslint/no-namespace */
import { type Brand, make } from "ts-brand";
import { type ServiceParameterGroup } from "./ServiceParameterGroup";

export type IndividualServiceDefinitionType = "fixed" | "hourly";

/**
 * The type of an individual service definition matches the id of the service definitions for individual services.
 *
 * NOTE: If you add a new config.json for an individual service definition, you must also add a new id here.
 *
 * @readonly
 * @enum {string} IndividualServiceDefinitionId
 */
export type IndividualServiceDefinitionId = "individual-fixed-price" | "individual-hourly-rate";

/**
 * A standard service definition is a digital representation of a service
 * that is offered by the service provider. It can be configured by
 * the user in the OrderConfigurator to request an individual quote
 * or to order the service.
 *
 * Service definitions are managed in the service definition repository.
 *
 * @see ServiceDefinitionRepository
 */
export interface ServiceDefinition {
    /**
     * The id to identify the service.
     *
     * Must be a non-empty ID string.
     */
    id: ServiceDefinition.Id;

    /**
     * The name presented to the user.
     *
     * Must be a non-empty string.
     */
    name: string;

    /**
     * An optional property to provide a comprehensive description of the service.
     */
    description?: string;

    /**
     * An optional property to provide a custom label for the action button in the catalog.
     */
    actionLabel?: string;

    /**
     * The groups of parameters that are provided to the user to configure the service.
     */
    parameterGroups: ServiceParameterGroup[];

    /**
     * An optional property to provide a design theme to style the service in
     * the OrderConfigurator.
     */
    theme?: string;

    /**
     * Determines how pricing is displayed to the customer.
     * - "show-price": Show calculated price immediately to all users (default)
     * - "show-disclosed-price": Show price only to logged-in customers after offer is provided
     * - "show-no-price": Hide price from customers; only Sales can see prices
     */
    requestPricing?: ServiceDefinition.RequestPricing;

    /**
     * Determines the quote/order workflow process.
     * - "calculated": Standard quote/order process (default)
     * - "individual": Provider must provide custom quote before customer sees price
     */
    quoteProcess?: ServiceDefinition.QuoteProcess;

    /**
     * Optional order field for sorting service definitions.
     * Lower values appear first. Items without order appear last.
     */
    order?: number;
}

export namespace ServiceDefinition {
    /**
     * The id to identify the service.
     *
     * Must be a non-empty ID string.
     */
    export type Id = Brand<string, ServiceDefinition>;

    export const getIndividualId = (type: IndividualServiceDefinitionType): Id => {
        return Id(type === "fixed" ? "individual-fixed-price" : "individual-hourly-rate");
    };

    /**
     * Creates a new service definition ID.
     *
     * @param value The value to create the ID from.
     * @returns The new service definition ID.
     * @throws An error if the ID is empty.
     */
    export const Id = make<Id>(value => {
        if (typeof value !== "string") {
            throw new Error("ServiceDefinition: Property 'id' must be a string.");
        }
        if (!value || value.trim() === "") {
            throw new Error("ServiceDefinition: Property 'id' cannot be empty.");
        }
        if (value.includes(":")) {
            throw new Error("ServiceDefinition: Property 'id' cannot be a custom service definition id.");
        }
        return value;
    });

    /**
     * Determines how pricing is displayed to the customer.
     * - "show-price": Show calculated price immediately to all users (default)
     * - "show-disclosed-price": Show price only to logged-in customers after offer is provided (order stage >= Offered)
     * - "show-no-price": Hide price from customers; only Sales can see prices
     */
    export type RequestPricing = "show-no-price" | "show-disclosed-price" | "show-price";

    /**
     * Determines the quote/order workflow process.
     * - "calculated": Standard quote/order process (default)
     * - "individual": Provider must provide custom quote before customer sees price
     */
    export type QuoteProcess = "calculated" | "individual";

    export const isValid = (value: unknown): value is ServiceDefinition => {
        return (
            typeof value === "object" &&
            value !== null &&
            "id" in value &&
            "name" in value &&
            isStandardServiceId(value.id)
        );
    };

    export const isStandardServiceId = (value: unknown): value is Id => {
        if (typeof value !== "string") {
            return false;
        }
        if (!value || value.trim() === "") {
            return false;
        }
        return !value.includes(":");
    };

    export const isIndividualServiceId = (value: unknown): value is Id => {
        return (
            typeof value === "string" &&
            (value.startsWith("individual-") || value === "custom") &&
            !value.includes(":")
        );
    };

    export const getIndividualType = (value: unknown): IndividualServiceDefinitionType | undefined => {
        if (!isIndividualServiceId(value)) {
            return undefined;
        }
        return value.split("-")[1] as IndividualServiceDefinitionType;
    };
}
